import { Tables, TablesInsert, TablesUpdate } from '@/supabase/types'
import { SupabaseClient } from '@supabase/supabase-js'

import { createServiceClient } from '@/lib/supabase/service-client'

type AssistantsClient = SupabaseClient

const getDefaultClient = (client?: AssistantsClient): AssistantsClient => {
  if (client) return client

  if (typeof window !== 'undefined') {
    const { supabase } = require('@/lib/supabase/browser-client') as {
      supabase: AssistantsClient
    }
    return supabase
  }

  return createServiceClient()
}

export const getAssistantById = async (assistantId: string, client?: SupabaseClient) => {
  const { data: assistant, error } = await getDefaultClient(client)
    .from('assistants')
    .select('*, messages (count)')
    .eq('id', assistantId)
    .single()

  if (!assistant) {
    throw new Error(error?.message)
  }

  return assistant
}

export const getAssistantByHashId = async (hashId: string, client?: SupabaseClient) => {
  const { data: assistant, error } = await getDefaultClient(client)
    .from('assistants')
    .select('*')
    .eq('hashid', hashId)
    .single()

  if (!assistant) {
    throw new Error(error?.message)
  }

  return assistant
}

export const getAssistantWorkspacesByWorkspaceId = async (
  workspaceId: string,
  client?: SupabaseClient
) => {
  const { data: workspace, error } = await getDefaultClient(client)
    .from('workspaces')
    .select(
      `
      id,
      name,
      assistants (*)
    `
    )
    .eq('id', workspaceId)
    .single()

  if (!workspace) {
    throw new Error(error.message)
  }

  return workspace.assistants as Tables<'assistants'>[]
}

export const getPrivatePopularAssistantsByUserId = async (
  userId: string,
  client?: SupabaseClient
) => {
  const { data: assistants, error } = await getDefaultClient(client)
    .from('assistants')
    .select('*, chats!inner(count)', { count: 'exact' })
    .eq('chats.user_id', userId)

  if (error) {
    throw new Error(error.message)
  }

  return assistants.sort((a, b) => b.chats[0].count - a.chats[0].count)
}

export const getPopularAssistants = async (
  workspaceId: string,
  userId: string,
  client?: SupabaseClient
) => {
  const { data: assistants, error } = await getDefaultClient(client).rpc('get_assistants_for_user', {
    p_workspace_id: workspaceId,
    p_user_id: userId
  })

  if (error) {
    throw new Error(error.message)
  }

  return assistants
}

export const getPublicAssistantsOrderedByMessageCountDesc = async (
  client?: SupabaseClient
) => {
  const { data: assistants, error } = await getDefaultClient(client)
    .from('assistants')
    .select('*, chats (count)', { count: 'exact' })
    .eq('sharing', 'public')
    .order('count', { ascending: false, referencedTable: 'chats' })

  if (error) {
    throw new Error(error.message)
  }

  return assistants.sort((a, b) => b.chats[0].count - a.chats[0].count)
}

export const getAssistantWorkspacesByAssistantId = async (assistantId: string) => {
  const { data: assistant, error } = await getDefaultClient()
    .from('assistants')
    .select(
      `
      id, 
      name, 
      workspaces (*)
    `
    )
    .eq('id', assistantId)
    .single()

  if (!assistant) {
    throw new Error(error.message)
  }

  return assistant
}

export const createAssistant = async (
  assistant: TablesInsert<'assistants'>,
  workspace_id: string
) => {
  const { data: createdAssistant, error } = await getDefaultClient()
    .from('assistants')
    .insert([assistant])
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  await createAssistantWorkspace({
    user_id: createdAssistant.user_id,
    assistant_id: createdAssistant.id,
    workspace_id
  })

  return createdAssistant
}

export const createAssistants = async (
  assistants: TablesInsert<'assistants'>[],
  workspace_id: string
) => {
  const { data: createdAssistants, error } = await getDefaultClient()
    .from('assistants')
    .insert(assistants)
    .select('*')

  if (error) {
    throw new Error(error.message)
  }

  await createAssistantWorkspaces(
    createdAssistants.map((assistant) => ({
      user_id: assistant.user_id,
      assistant_id: assistant.id,
      workspace_id
    }))
  )

  return createdAssistants
}

export const createAssistantWorkspace = async (item: {
  user_id: string
  assistant_id: string
  workspace_id: string
}) => {
  const { data: createdAssistantWorkspace, error } = await getDefaultClient()
    .from('assistant_workspaces')
    .insert([item])
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return createdAssistantWorkspace
}

export const createAssistantWorkspaces = async (
  items: { user_id: string; assistant_id: string; workspace_id: string }[]
) => {
  const { data: createdAssistantWorkspaces, error } = await getDefaultClient()
    .from('assistant_workspaces')
    .insert(items)
    .select('*')

  if (error) throw new Error(error.message)

  return createdAssistantWorkspaces
}

export const updateAssistant = async (
  assistantId: string,
  assistant: TablesUpdate<'assistants'>
) => {
  const { data: updatedAssistant, error } = await getDefaultClient()
    .from('assistants')
    .update(assistant)
    .eq('id', assistantId)
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return updatedAssistant
}

export const deleteAssistant = async (assistantId: string) => {
  const { error } = await getDefaultClient().from('assistants').delete().eq('id', assistantId)

  if (error) {
    throw new Error(error.message)
  }

  return true
}

export const deleteAssistantWorkspace = async (assistantId: string, workspaceId: string) => {
  const { error } = await getDefaultClient()
    .from('assistant_workspaces')
    .delete()
    .eq('assistant_id', assistantId)
    .eq('workspace_id', workspaceId)

  if (error) throw new Error(error.message)

  return true
}
